import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { useLocale } from 'src/contexts/LocaleContext';

const flagClass = 'h-3.5 w-5 shrink-0 overflow-hidden rounded-[2px] ring-1 ring-black/10';

function UnitedStatesFlag() {
  const stripeHeight = 30 / 13;
  const cantonHeight = stripeHeight * 7;
  const rowCount = 9;
  const stars = [];
  for (let row = 0; row < rowCount; row += 1) {
    const count = row % 2 === 0 ? 6 : 5;
    const y = ((row + 1) / (rowCount + 1)) * cantonHeight;
    for (let col = 0; col < count; col += 1) {
      const x = count === 6 ? ((col + 0.5) / 6) * 24 : ((col + 1) / 6) * 24;
      stars.push({ x, y, key: `${row}-${col}` });
    }
  }

  return (
    <svg viewBox="0 0 60 30" className={flagClass} preserveAspectRatio="none" aria-hidden="true">
      {Array.from({ length: 13 }, (_, index) => (
        <rect
          key={index}
          y={index * stripeHeight}
          width="60"
          height={stripeHeight + 0.15}
          fill={index % 2 === 0 ? '#B22234' : '#FFFFFF'}
        />
      ))}
      <rect width="24" height={cantonHeight} fill="#3C3B6E" />
      {stars.map((star) => (
        <circle key={star.key} cx={star.x} cy={star.y} r="0.55" fill="#FFFFFF" />
      ))}
    </svg>
  );
}

function LocaleFlag({ id }) {
  if (id === 'en') {
    return <UnitedStatesFlag />;
  }

  if (id === 'es') {
    return (
      <svg viewBox="0 0 18 12" className={flagClass} aria-hidden="true">
        <rect width="18" height="12" fill="#aa151b" />
        <rect y="3" width="18" height="6" fill="#f1bf00" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 18 12" className={flagClass} aria-hidden="true">
      <rect width="18" height="12" fill="#009b3a" />
      <polygon points="9,1.15 16.6,6 9,10.85 1.4,6" fill="#fedd00" />
      <circle cx="9" cy="6" r="2.15" fill="#002776" />
    </svg>
  );
}

const LanguageSelector = ({ compact = false, dropUp = false, fullWidth = false }) => {
  const { locale, setLocale, locales } = useLocale();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const current = locales.find((item) => item.id === locale) || locales[0];

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const onKey = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={current.label}
        onClick={() => setOpen((value) => !value)}
        className={`inline-flex items-center border border-[#ede9fe] bg-white font-semibold text-[#0f172a] ${
          fullWidth ? 'w-full justify-between' : ''
        } ${
          compact
            ? 'gap-1.5 rounded-lg px-2.5 py-1.5 text-xs'
            : 'gap-1.5 rounded-[10px] px-3 py-2 text-[13px]'
        }`}
      >
        <span className="inline-flex min-w-0 items-center gap-1.5">
          <LocaleFlag id={current.id} />
          <span>{current.label}</span>
        </span>
        <ChevronDown className={`size-3 shrink-0 ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>
      {open && (
        <ul
          role="listbox"
          className={`absolute z-50 min-w-full rounded-lg border border-[#ede9fe] bg-white p-1.5 shadow-[0px_6px_8px_rgba(0,0,0,0.08)] ${
            dropUp ? 'bottom-full left-0 mb-1' : 'right-0 mt-1'
          }`}
        >
          {locales.map((item) => {
            const selected = item.id === locale;
            return (
              <li key={item.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={selected}
                  onClick={() => {
                    setLocale(item.id);
                    setOpen(false);
                  }}
                  className={`flex w-full items-center gap-2 whitespace-nowrap rounded-md px-2 py-1.5 text-left text-xs ${
                    selected
                      ? 'font-semibold text-[#7c3aed]'
                      : 'font-medium text-[#4b5563] hover:bg-[#faf8fc]'
                  }`}
                >
                  <LocaleFlag id={item.id} />
                  <span>{item.label}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default LanguageSelector;
